import { useEffect, useState } from 'react';
import { AlertTriangle, Info, LayoutList, Loader2 } from 'lucide-react';
import { apiClient } from '../../../api';
import DisagreementLane from './DisagreementLane';

interface ConsoleBand {
  band: number;
  title: string;
  action: string;
  count: number;
  measured: { migrate_rate: number | null; migrated: number; labelled: number; insufficient: boolean; label: string } | null;
}

interface ConsoleData {
  scope: string;
  basis: string;
  basis_predicate: string;
  extract: { batch_id: number; as_of: string | null } | null;
  scorer_available: boolean;
  caveat: string | null;
  bands: ConsoleBand[];
  band5_by_datacentre: { dc: string; count: number }[];
  interview_capacity: { queue: number; slots_per_week: number; weeks_to_clear: number | null };
  thresholds: { likely_migrates: number; likely_excluded: number };
}

export default function ManagementConsole({ refreshKey, scope, scopeLoaded, basis }: {
  refreshKey: number;
  scope: string | null;
  scopeLoaded: boolean;
  basis: string;
}) {
  const [data, setData] = useState<ConsoleData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [slots, setSlots] = useState('');
  const [savingSlots, setSavingSlots] = useState(false);

  useEffect(() => {
    if (!scopeLoaded) return undefined;
    if (!scope) { setData(null); setLoading(false); return undefined; }
    let cancelled = false;
    setLoading(true);
    apiClient.get('/api/cloud-portfolio/console', { params: { scope, basis } })
      .then((r) => {
        if (cancelled) return;
        setData(r.data);
        setSlots(String(r.data?.interview_capacity?.slots_per_week ?? 0));
        setError(null);
      })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.error || e.message || 'Failed to load the console'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [scope, scopeLoaded, basis, refreshKey]);

  const saveSlots = async () => {
    const n = Number(slots);
    if (!Number.isInteger(n) || n < 0) return;
    setSavingSlots(true);
    try {
      await apiClient.patch('/api/cloud-portfolio/console/settings', { interview_slots_per_week: n });
      setData((d) => d ? {
        ...d,
        interview_capacity: {
          ...d.interview_capacity,
          slots_per_week: n,
          weeks_to_clear: n > 0 ? Math.ceil(d.interview_capacity.queue / n) : null,
        },
      } : d);
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'Failed to save capacity');
    } finally {
      setSavingSlots(false);
    }
  };

  const ratePct = (b: ConsoleBand) => {
    if (!b.measured || b.measured.migrate_rate == null) return '—';
    if (b.measured.insufficient) return `insufficient data (n=${b.measured.labelled})`;
    return `${b.measured.migrated} of ${b.measured.labelled} · ${Math.round(b.measured.migrate_rate * 100)}% ${b.measured.label}`;
  };

  if (!scopeLoaded || loading) return <p className="text-xs text-gray-500 flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading {scope || ''} console…</p>;

  if (!scope) {
    return (
      <div className="flex items-start gap-2 text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2.5">
        <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
        Pick a datacentre on the Scope tab first.
      </div>
    );
  }

  if (!data) return null;

  const maxDc = Math.max(1, ...data.band5_by_datacentre.map((d) => d.count));

  return (
    <div className="space-y-5">
      {error && (
        <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
          <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          {error}
        </div>
      )}

      {data.caveat && (
        <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
          <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
          {data.caveat}
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-1">
          <LayoutList className="w-4 h-4 text-amber-500" />
          <span className="text-sm font-semibold text-super-dark-purple">Queue · {data.scope} — ranked by expected yield</span>
        </div>
        <p className="text-[11px] text-gray-400 mb-1">Basis {data.basis} ({data.basis_predicate}) · extract as of {data.extract?.as_of || '—'}</p>
        <p className="text-xs text-gray-500 mb-3">Every band names its required action and carries its measured rate — recomputed from the labelled set, never hardcoded.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-gray-500 border-b">
              <tr>
                <th className="text-left py-1.5 pr-3">Band</th>
                <th className="text-right pr-4">Apps</th>
                <th className="text-right pr-4">Measured</th>
                <th className="text-left">Required action</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.bands.map((b) => (
                <tr key={b.band}>
                  <td className="py-2 pr-3 whitespace-nowrap font-semibold text-gray-700">{b.band} · {b.title}</td>
                  <td className="text-right pr-4 font-bold text-super-dark-purple">{b.count}</td>
                  <td className="text-right pr-4 text-gray-600 whitespace-nowrap">{ratePct(b)}</td>
                  <td className="text-gray-600">{b.action}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!data.scorer_available && (
          <p className="text-[11px] text-amber-700 mt-2">No scorer trained yet — bands 3/4/5 fall back to interview-required.</p>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-sm font-semibold text-super-dark-purple mb-1">Interview queue by datacentre</p>
          <p className="text-xs text-gray-500 mb-3">Band 5 across every datacentre — this is how the next wave gets planned.</p>
          <div className="space-y-1.5">
            {data.band5_by_datacentre.map((d) => (
              <div key={d.dc} className="flex items-center gap-2 text-xs">
                <span className="w-40 truncate text-gray-600">{d.dc}</span>
                <div className="flex-1 h-4 bg-gray-100 rounded overflow-hidden">
                  <div className="h-full rounded" style={{ width: `${Math.max(4, (d.count / maxDc) * 100)}%`, background: d.dc === data.scope ? '#221551' : '#AFA9EC' }} />
                </div>
                <span className="w-8 text-right font-semibold text-gray-700">{d.count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <p className="text-sm font-semibold text-super-dark-purple mb-1">Interview capacity · {data.scope}</p>
          <p className="text-xs text-gray-500 mb-3">The trade-off between confidence and throughput, made explicit.</p>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <p className="text-[11px] text-gray-500">Interview queue</p>
              <p className="text-xl font-bold text-super-dark-purple">{data.interview_capacity.queue}</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <p className="text-[11px] text-gray-500">Slots / week</p>
              <p className="text-xl font-bold text-super-dark-purple">{data.interview_capacity.slots_per_week || '—'}</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <p className="text-[11px] text-gray-500">Weeks to clear</p>
              <p className="text-xl font-bold text-super-dark-purple">{data.interview_capacity.weeks_to_clear ?? '—'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-gray-600">Available interview slots per week</span>
            <input
              value={slots}
              onChange={(e) => setSlots(e.target.value.replace(/[^0-9]/g, ''))}
              className="w-20 border border-gray-300 rounded px-2 py-1.5 text-right"
            />
            <button
              onClick={saveSlots}
              disabled={savingSlots}
              className="px-2.5 py-1.5 rounded bg-amber-500 hover:bg-amber-600 text-white font-semibold disabled:opacity-50"
            >
              {savingSlots ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      <DisagreementLane refreshKey={refreshKey} scope={data.scope} basis={basis} />
    </div>
  );
}
